import { NotFoundError } from '@/features/shared/utils/setPageProps'
import { createServerSidePropsHandler } from '@/features/shared/serverUtils/ssrHelpers/createServerSidePropsHandler'
import { withoutHeaderLayout } from '@/features/shared/components/Headers/Layouts/withoutHeaderLayout'
import { ProductDetailPage } from '@/features/shop/components/ProductDetail/ProductDetailPage'
import { isUniversalProductId, resolveProductRouteName } from '@shared/utils/productRoute'
import { type GetServerSidePropsContext } from 'next'
import { getUniversalProductSSRProps } from '@/features/shared/serverUtils/ssrHelpers/getUniversalProductSSRProps'
import { getProductSSRProps } from '@/features/shared/serverUtils/ssrHelpers/getProductSSRProps'
import { getLegacyPdpRedirect } from '@/features/shared/serverUtils/ssrHelpers/getLegacyPdpRedirect'
import { subscriptionPlansGuestQueryOptions } from '@/features/account/services/SubscriptionAux/queries'
import { noop } from '@/features/shared/utils/noop'

type ProductProps =
  | {
      isUp: true
      isGone: boolean
      zipFromIp: string
      productId?: never
    }
  | {
      isUp?: never
      productId: number
      zipFromIp?: never
      isGone?: never
    }
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- disable since we are still using createServerSidePropsHandler
export const getServerSideProps = (context: GetServerSidePropsContext) => {
  const route = resolveProductRouteName(context.params?.id)

  return createServerSidePropsHandler<ProductProps>(route, async (props) => {
    const {
      ssrContext,
      contextualRedirect,
      queryClient,
      authContext: { isAuthenticated },
    } = props
    const { params } = ssrContext

    if (isUniversalProductId(params?.id)) {
      const [universalProductProps] = await Promise.all([
        getUniversalProductSSRProps(props, params.id),
        !isAuthenticated
          ? queryClient.query(subscriptionPlansGuestQueryOptions).catch(noop)
          : undefined,
      ])
      if ('redirect' in universalProductProps) return universalProductProps
      return {
        props: {
          ...universalProductProps.props,
          isUp: true,
        },
      }
    }

    const productId = Number(params?.id)
    if (!params?.id || isNaN(productId)) {
      if (contextualRedirect) return contextualRedirect
      throw new NotFoundError(`Invalid product id: "${productId}"`)
    }

    // Legacy /shop/products/[productId] urls (no uuid) are still crawled. Resolve the
    // product's universalId and permanently redirect to the canonical PDP url when found.
    const legacyRedirect = await getLegacyPdpRedirect(props, productId)
    if (legacyRedirect) return legacyRedirect

    const [productProps] = await Promise.all([
      getProductSSRProps(props, productId),
      ssrContext.query.region_id && !isAuthenticated
        ? queryClient.query(subscriptionPlansGuestQueryOptions).catch(noop)
        : undefined,
    ])

    if ('redirect' in productProps) return productProps

    return {
      props: {
        productId,
      },
    }
  })(context)
}

// due to the weirdness of routing for PDP, we have to suppress the footer both for this route and the other variable slug route, and have the NewFooter rendered within the PDP components.
export default withoutHeaderLayout(ProductDetailPage, { suppressFooter: true })
